// LT-657 — "Add to list" bulk action from the Contacts page.
//
// Given a set of selected contacts, the operator either creates a new
// static contact list with them as the roster, or appends them to an
// existing static list. Mirrors AddToCampaignModal's shape. Dynamic lists
// are not offered — their membership is filter-driven, so you can't add a
// fixed roster to them.

import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { contactListsAPI } from '../services/contactListsAPI';
import type { ContactList } from '../types/contactList';
import { Button, IconButton, Input, Radio, Select } from '../ui';

export interface AddToListModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactIds: string[];
  setToast: (t: { type: 'success' | 'error'; message: string }) => void;
  /** Called after a successful add so the caller can clear its selection. */
  onAdded?: () => void;
}

type Mode = 'new' | 'existing';

function extractErr(e: unknown, fallback: string): string {
  const ax = e as { response?: { data?: { detail?: { message?: string } | string } } };
  const d = ax?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && d.message) return d.message;
  return e instanceof Error ? e.message : fallback;
}

const AddToListModal: React.FC<AddToListModalProps> = ({
  isOpen,
  onClose,
  contactIds,
  setToast,
  onAdded,
}) => {
  const [mode, setMode] = useState<Mode>('new');
  const [newName, setNewName] = useState('');
  const [lists, setLists] = useState<ContactList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [targetListId, setTargetListId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = contactIds.length;

  useEffect(() => {
    if (!isOpen) return;
    setMode('new');
    setNewName('');
    setTargetListId('');
    setError(null);
    setListsLoading(true);
    contactListsAPI
      .list({ limit: 200 })
      // Static only — dynamic lists are filter-driven, not explicit rosters.
      .then((rows) => setLists(rows.filter((l) => l.list_type === 'static')))
      .catch(() => setLists([]))
      .finally(() => setListsLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, busy, onClose]);

  const handleConfirm = useCallback(async () => {
    if (count === 0) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'new') {
        const name = newName.trim();
        if (!name) {
          setError('Enter a list name.');
          setBusy(false);
          return;
        }
        const list = await contactListsAPI.create({
          name,
          list_type: 'static',
          description: 'Created from a contacts selection.',
        });
        await contactListsAPI.addMembers(list.id, { contact_ids: contactIds });
        setToast({
          type: 'success',
          message: `Created list "${name}" with ${count} contact${count === 1 ? '' : 's'}.`,
        });
      } else {
        if (!targetListId) {
          setError('Pick a list.');
          setBusy(false);
          return;
        }
        const targetName = lists.find((l) => l.id === targetListId)?.name ?? 'list';
        const res = await contactListsAPI.addMembers(targetListId, {
          contact_ids: contactIds,
        });
        setToast({
          type: 'success',
          message: `Added ${res.affected} new contact${res.affected === 1 ? '' : 's'} to "${targetName}".`,
        });
      }
      onAdded?.();
      onClose();
    } catch (e) {
      setError(extractErr(e, 'Failed to add contacts to a list.'));
    } finally {
      setBusy(false);
    }
  }, [mode, newName, targetListId, lists, contactIds, count, setToast, onAdded, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-to-list-title"
    >
      <div
        className="w-full max-w-md bg-surface-1 border border-white/[0.16] rounded-ds-xl p-5 shadow-ds-e3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="add-to-list-title" className="text-lg font-semibold text-slate-50">
            Add {count} contact{count === 1 ? '' : 's'} to a list
          </h2>
          <IconButton
            intent="ghost"
            size="sm"
            aria-label="Close"
            onClick={() => !busy && onClose()}
            disabled={busy}
          >
            <X size={16} />
          </IconButton>
        </div>

        {error ? (
          <div className="mb-3 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-sm">
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Radio
              name="add-to-list-mode"
              checked={mode === 'new'}
              onChange={() => setMode('new')}
              disabled={busy}
              label="Create a new list"
            />
            <Radio
              name="add-to-list-mode"
              checked={mode === 'existing'}
              onChange={() => setMode('existing')}
              disabled={busy}
              label="Add to an existing list"
            />
          </div>

          {mode === 'new' ? (
            <Input
              label="List name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. NCR developers — June batch"
              disabled={busy}
              maxLength={255}
            />
          ) : (
            <div>
              <Select
                label="List"
                value={targetListId}
                onChange={(e) => setTargetListId(e.target.value)}
                disabled={busy || listsLoading}
              >
                <option value="">
                  {listsLoading ? 'Loading…' : '— Select a list —'}
                </option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
              {!listsLoading && lists.length === 0 ? (
                <p className="text-xs text-slate-500 mt-1">
                  No static lists yet. Create a new one instead.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button intent="ghost" onClick={() => !busy && onClose()} disabled={busy}>
            Cancel
          </Button>
          <Button
            intent="primary"
            onClick={() => void handleConfirm()}
            disabled={busy || count === 0}
          >
            {busy ? 'Working…' : mode === 'new' ? 'Create list' : 'Add to list'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddToListModal;
